import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  createBreakId,
  DeclinedUser,
  FirestoreCollection,
  HubblFilter,
  HubblMessage,
  Palette,
  HubblTask,
  User,
} from 'hubbl-shared';
import {HubblGroupDTO} from '../models';
import {firestore} from './firebase';
import {mapUserToUserDTO} from './utils/map-incoming-users/mapUserToUserDTO';

interface CreateHubblGroupRequest {
  title: string;
  description: string;
  activity: string;
  emoji: string;
  startDateTime: string;
  endDateTime?: string;
  maxParticipants: number | null;
  location?: string;
  extraDetails?: string;
  invitedUsers: User[];
  filters: HubblFilter[];
  officeId?: string;
  spaceId?: string;
  creator: User;
  messages: HubblMessage[];
  isRepeatEnabled: boolean;
  tasks: HubblTask[];
}

const getPendingHubbles = async (
  companyId: string,
  userId: string,
): Promise<HubblGroupDTO[]> => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const nowTimestamp = Timestamp.fromDate(now);

  const hubblRef = collection(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
  );

  let hubblQuery = query(
    hubblRef,
    where('isGroupFilled', '==', false),
    where('startDateTime', '>=', nowTimestamp),
  );

  const snapshot = await getDocs(hubblQuery);
  let hubbles = snapshot.docs.map(doc => doc.data() as HubblGroupDTO);

  // Filter out hubbles the user is already part of (those are returned by getScheduledHubbles)
  hubbles = hubbles.filter(hubbl => !hubbl.attendeeIds?.includes(userId));

  return hubbles;
};

const getScheduledHubbles = (
  companyId: string,
  userId: string,
  callback: (hubbles: HubblGroupDTO[], isDeleted: boolean) => void,
) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const nowTimestamp = Timestamp.fromDate(now);

  const hubblRef = collection(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
  );

  const hubblQuery = query(
    hubblRef,
    where('attendeeIds', 'array-contains', userId),
    where('startDateTime', '>=', nowTimestamp),
  );

  return onSnapshot(hubblQuery, snapshot => {
    let isDeleted = false;

    if (snapshot.docChanges().some(change => change.type === 'removed')) {
      isDeleted = true;
    }

    const hubbles = snapshot.docs.map(doc => doc.data() as HubblGroupDTO);

    callback(hubbles, isDeleted);
  });
};

const createHubblGroup = async (
  companyId: string,
  groupData: CreateHubblGroupRequest,
): Promise<HubblGroupDTO> => {
  const hubblRef = collection(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
  );

  const createdNowDate = new Date();
  const hubblId = createBreakId({
    breakCreated: createdNowDate,
    userId: groupData.creator.id,
  });

  const hubblGroup: HubblGroupDTO = {
    id: hubblId,
    type: 'group',
    location: groupData.location || null,
    description: {en: groupData.description, da: groupData.description},
    emoji: groupData.emoji,
    maxAttendees: groupData.maxParticipants,
    isGroupFilled: false,
    color: Palette.primaryPadua,
    extraDetails: groupData.extraDetails
      ? {en: groupData.extraDetails, da: groupData.extraDetails}
      : null,
    startDateTime: Timestamp.fromDate(new Date(groupData.startDateTime)),
    endDateTime: groupData.endDateTime
      ? Timestamp.fromDate(new Date(groupData.endDateTime))
      : null,
    createdDateTime: Timestamp.fromDate(createdNowDate),
    creator: mapUserToUserDTO(groupData.creator),
    attendees: [mapUserToUserDTO(groupData.creator)],
    attendeeIds: [groupData.creator.id],
    labels: [],
    companyId,
    isHubblPassed: false,
    isRepeatEnabled: groupData.isRepeatEnabled,
    isRepeatedHubbl: false,
    messages: groupData.messages,
    officeId: groupData.creator.settings?.currentOfficeId || 'default',
    spaceId: groupData.creator.settings?.currentSpaceId || 'default',
    filters: groupData.filters || [],
    invitedUsers:
      groupData.invitedUsers?.map(user => {
        return {
          id: user.id,
          fullName: user.fullName,
          firstName: user.firstName,
          photoUrl: user.photoUrl,
          department: user.department,
          email: user.email,
        };
      }) || [],
    declinedUserIds: [],
    declinedUsers: [],
    tasks: groupData.tasks || [],
  };

  const hubblDocRef = doc(hubblRef, hubblId);
  await setDoc(hubblDocRef, hubblGroup);

  return hubblGroup;
};

const joinHubblGroup = async (
  companyId: string,
  hubblId: string,
  user: User,
): Promise<void> => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  const userDto = mapUserToUserDTO(user);

  await runTransaction(firestore, async transaction => {
    const hubblSnap = await transaction.get(hubblRef);
    if (!hubblSnap.exists()) {
      throw new Error('Hubbl group not found');
    }

    const hubblData = hubblSnap.data() as HubblGroupDTO;

    // Filter out the user from declined arrays
    const updatedDeclinedUserIds =
      hubblData.declinedUserIds?.filter(id => id !== user.id) || [];
    const updatedDeclinedUsers =
      hubblData.declinedUsers?.filter(
        declinedUser => declinedUser.user.id !== user.id,
      ) || [];

    // Add user to attendees if not already present
    const updatedAttendeeIds = hubblData.attendeeIds?.includes(user.id)
      ? hubblData.attendeeIds
      : [...(hubblData.attendeeIds || []), user.id];

    const updatedAttendees = hubblData.attendees?.some(
      attendee => attendee.id === user.id,
    )
      ? hubblData.attendees
      : [...(hubblData.attendees || []), userDto];

    transaction.update(hubblRef, {
      attendeeIds: updatedAttendeeIds,
      attendees: updatedAttendees,
      declinedUserIds: updatedDeclinedUserIds,
      declinedUsers: updatedDeclinedUsers,
    });
  });
};

const leaveHubblGroup = async (
  companyId: string,
  hubblId: string,
  userId: string,
): Promise<void> => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  await runTransaction(firestore, async transaction => {
    const hubblSnap = await transaction.get(hubblRef);
    if (!hubblSnap.exists()) {
      throw new Error('Hubbl group not found');
    }

    const hubblData = hubblSnap.data() as HubblGroupDTO;

    // Filter out the user from attendees
    const updatedAttendeeIds =
      hubblData.attendeeIds?.filter(id => id !== userId) || [];
    const updatedAttendees =
      hubblData.attendees?.filter(attendee => attendee.id !== userId) || [];

    transaction.update(hubblRef, {
      attendeeIds: updatedAttendeeIds,
      attendees: updatedAttendees,
    });
  });
};

const declineHubbl = async (
  companyId: string,
  hubblId: string,
  user: User,
): Promise<void> => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  const declinedUser: DeclinedUser = {
    declinedAt: new Date().toISOString(),
    user: {
      id: user.id,
      fullName: user.fullName,
      firstName: user.firstName,
      photoUrl: user.photoUrl,
      email: user.email,
      department: user.department,
    },
  };

  await runTransaction(firestore, async transaction => {
    const hubblSnap = await transaction.get(hubblRef);
    if (!hubblSnap.exists()) {
      throw new Error('Hubbl group not found');
    }

    const hubblData = hubblSnap.data() as HubblGroupDTO;

    // Remove user from attendees
    const updatedAttendeeIds =
      hubblData.attendeeIds?.filter(id => id !== user.id) || [];
    const updatedAttendees =
      hubblData.attendees?.filter(attendee => attendee.id !== user.id) || [];

    // Add user to declined arrays if not already present
    const updatedDeclinedUserIds = hubblData.declinedUserIds?.includes(user.id)
      ? hubblData.declinedUserIds
      : [...(hubblData.declinedUserIds || []), user.id];

    const updatedDeclinedUsers = hubblData.declinedUsers?.some(
      declined => declined.user.id === user.id,
    )
      ? hubblData.declinedUsers
      : [...(hubblData.declinedUsers || []), declinedUser];

    transaction.update(hubblRef, {
      attendeeIds: updatedAttendeeIds,
      attendees: updatedAttendees,
      declinedUserIds: updatedDeclinedUserIds,
      declinedUsers: updatedDeclinedUsers,
    });
  });
};

const undeclineHubbl = async (
  companyId: string,
  hubblId: string,
  user: User,
): Promise<void> => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  await runTransaction(firestore, async transaction => {
    const hubblSnap = await transaction.get(hubblRef);
    if (!hubblSnap.exists()) {
      throw new Error('Hubbl group not found');
    }

    const hubblData = hubblSnap.data() as HubblGroupDTO;

    // Remove user from declined arrays
    const updatedDeclinedUserIds =
      hubblData.declinedUserIds?.filter(id => id !== user.id) || [];
    const updatedDeclinedUsers =
      hubblData.declinedUsers?.filter(
        declinedUser => declinedUser.user.id !== user.id,
      ) || [];

    transaction.update(hubblRef, {
      declinedUserIds: updatedDeclinedUserIds,
      declinedUsers: updatedDeclinedUsers,
    });
  });
};

const deleteHubbl = async (
  companyId: string,
  hubblId: string,
): Promise<void> => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  await deleteDoc(hubblRef);
};

const getHubbl = async (
  companyId: string,
  hubblId: string,
): Promise<HubblGroupDTO | null> => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  const snapshot = await getDoc(hubblRef);

  if (snapshot.exists()) {
    return snapshot.data() as HubblGroupDTO;
  }

  return null;
};

const addHubblMessage = async (
  companyId: string,
  hubblId: string,
  message: HubblMessage,
): Promise<void> => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  await updateDoc(hubblRef, {
    messages: arrayUnion(message),
  });
};

const onHubblMessagesChangeListener = (
  companyId: string,
  hubblId: string,
  callback: (messages: HubblMessage[]) => void,
) => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  return onSnapshot(hubblRef, snapshot => {
    if (snapshot.exists()) {
      const hubblData = snapshot.data() as HubblGroupDTO;
      callback(hubblData.messages || []);
    }
  });
};

// Task management functions
const addHubblTask = async (
  companyId: string,
  hubblId: string,
  taskTitle: string,
  creatorId: string,
): Promise<void> => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  const newTask: HubblTask = {
    id: createBreakId({
      breakCreated: new Date(),
      userId: creatorId,
    }),
    title: taskTitle,
    assignedUserId: null,
    createdDateTime: new Date().toISOString(),
    assignedDateTime: null,
  };

  await updateDoc(hubblRef, {
    tasks: arrayUnion(newTask),
  });
};

const updateHubblTask = async (
  companyId: string,
  hubblId: string,
  taskId: string,
  updates: Partial<HubblTask>,
): Promise<void> => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  await runTransaction(firestore, async transaction => {
    const hubblSnap = await transaction.get(hubblRef);
    if (!hubblSnap.exists()) {
      throw new Error('Hubbl not found');
    }

    const hubblData = hubblSnap.data();
    const tasks = hubblData.tasks || [];
    const taskIndex = tasks.findIndex((task: HubblTask) => task.id === taskId);

    if (taskIndex === -1) {
      throw new Error('Task not found');
    }

    const updatedTask = {
      ...tasks[taskIndex],
      ...updates,
    };

    const updatedTasks = [...tasks];
    updatedTasks[taskIndex] = updatedTask;

    transaction.update(hubblRef, {
      tasks: updatedTasks,
    });
  });
};

const deleteHubblTask = async (
  companyId: string,
  hubblId: string,
  taskId: string,
): Promise<void> => {
  const hubblRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Hubbles,
    hubblId,
  );

  await runTransaction(firestore, async transaction => {
    const hubblSnap = await transaction.get(hubblRef);
    if (!hubblSnap.exists()) {
      throw new Error('Hubbl not found');
    }

    const hubblData = hubblSnap.data();
    const tasks = hubblData.tasks || [];
    const updatedTasks = tasks.filter((task: HubblTask) => task.id !== taskId);

    transaction.update(hubblRef, {
      tasks: updatedTasks,
    });
  });
};

export const hubbl = {
  getPendingHubbles,
  getScheduledHubbles,
  createHubblGroup,
  joinHubblGroup,
  leaveHubblGroup,
  declineHubbl,
  undeclineHubbl,
  deleteHubbl,
  getHubbl,
  addHubblMessage,
  onHubblMessagesChangeListener,
  addHubblTask,
  updateHubblTask,
  deleteHubblTask,
};
